export const META_PIXEL_ID = '1070366125738584'

type FbqFn = (...args: unknown[]) => void

declare global {
  interface Window {
    fbq?: FbqFn & { callMethod?: FbqFn; queue?: unknown[] }
    _fbq?: unknown
  }
}

/* Module-level flags: they survive React re-renders and StrictMode's
   double-invoked effects, so each entry event can only fire once per load. */
let initialized = false
let pageViewFired = false
let viewContentFired = false
let lastInitiateCheckoutAt = 0

/**
 * Injects the official Meta Pixel base code and initializes the pixel.
 * Safe to call multiple times — it only runs once.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  const w = window as any

  if (!w.fbq) {
    const n: any = function () {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    w.fbq = n
    w._fbq = n
  }

  const existing = document.querySelector<HTMLScriptElement>(
    'script[data-meta-pixel="true"]',
  )

  if (!existing) {
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    script.setAttribute('data-meta-pixel', 'true')
    // Append to head (or body/documentElement as fallbacks) instead of
    // relying on an existing <script> tag being present in the document.
    const host = document.head || document.body || document.documentElement
    host.appendChild(script)
  }

  window.fbq?.('init', META_PIXEL_ID)
}

/** Fires a standard Meta Pixel event. */
export function trackEvent(
  event: string,
  params?: Record<string, unknown>,
  options?: { eventID: string },
): void {
  if (typeof window === 'undefined') return
  if (options) {
    window.fbq?.('track', event, params, options)
  } else {
    window.fbq?.('track', event, params)
  }
}

/** Fires a custom (non-standard) Meta Pixel event. */
export function trackCustomEvent(
  event: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return
  window.fbq?.('trackCustom', event, params)
}

/** Unique id per event, used by Meta to de-duplicate repeated deliveries. */
function eventId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export const PRODUCT = {
  content_name: 'Metabolismo Reset',
  content_ids: ['metabolismo-restart-pro'],
  content_type: 'product',
  value: 12.99,
  currency: 'USD',
}

/** PageView — once per page load. */
export function trackPageView(): void {
  if (pageViewFired) return
  pageViewFired = true
  trackEvent('PageView', undefined, { eventID: eventId('pv') })
}

/** ViewContent — once per page load, after the landing has rendered. */
export function trackViewContent(): void {
  if (viewContentFired) return
  viewContentFired = true
  trackEvent('ViewContent', PRODUCT, { eventID: eventId('vc') })
}

/**
 * InitiateCheckout — fired on checkout CTA clicks.
 * Repeated clicks within 1s (double taps, bubbling) are ignored.
 */
export function trackInitiateCheckout(location: string): void {
  const now = Date.now()
  if (now - lastInitiateCheckoutAt < 1000) return
  lastInitiateCheckoutAt = now
  trackEvent(
    'InitiateCheckout',
    { ...PRODUCT, cta_location: location },
    { eventID: eventId('ic') },
  )
}
